import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { LocalizedLink } from 'src/components/common/LocalizedLink';
import { getNeighborhoodList } from '@/API/apiService';
import Picture from '@/image/hotel/picture9.png';
import { useLocation } from 'react-router-dom';

// Swiperのスタイルをインポート
import 'swiper/css';
import 'swiper/css/pagination';

interface Tag {
  id: number;
  name: string;
  name_en: string;
}

interface SlideData {
  imageSrc: string;
  width: number;
  height: number;
  linkText: string; // list_title or list_title_en
  date: string; // date_time
  description: string; // title or title_en
  readStoryLink: string;
  tags?: Tag[]; // タグ配列
}

interface SlideItemProps extends SlideData {
  useEnglish: boolean;
}

const LoadingSpinner: React.FC = () => (
  <div className="flex justify-center items-center py-10">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900" />
    <span className="ml-3">Loading...</span>
  </div>
);

const SlideItem: React.FC<SlideItemProps> = ({
  imageSrc,
  width,
  height,
  linkText,
  date,
  description,
  readStoryLink,
  tags,
  useEnglish,
}) => {
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.target as HTMLImageElement).src = Picture;
  };

  // 日付表示用（必要に応じて）
  const displayDate = date ? date.slice(0, 10) : '';

  return (
    <div className="py-2 mb-5" style={{ width: `${width}px` }}>
      <LocalizedLink to={readStoryLink}>
        <div className="flex justify-center items-center">
          <img
            src={imageSrc}
            alt={linkText}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              objectFit: 'cover',
            }}
            onError={handleImageError}
          />
        </div>

        <div className="mt-2 pb-8">
          {/* タイトルや日時 */}
          <div className="flex justify-between items-center text-base">
            <span>{linkText}</span>
            <span className="text-xs text-gray-500">{displayDate}</span>
          </div>

          {/* タグの表示：言語に応じて切り替え */}
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap mt-1">
              {tags.map((tag) => {
                const displayTag =
                  useEnglish && tag.name_en ? tag.name_en : tag.name;
                return (
                  <span
                    key={tag.id}
                    className="text-[10px] underline mr-2 mb-5"
                  >
                    {displayTag}
                  </span>
                );
              })}
            </div>
          )}

          <span className="underline block md:hidden text-xs">Read story</span>
        </div>
      </LocalizedLink>
    </div>
  );
};

export const StorySlider: React.FC = () => {
  // URLの先頭セグメントから言語を取得
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  const [slidesData, setSlidesData] = useState<SlideData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    getNeighborhoodList()
      .then((res) => {
        if (res.data?.data && Array.isArray(res.data.data)) {
          const sortedData = [...res.data.data].sort((a, b) => {
            const dateA = new Date(a.date_time).getTime() || 0;
            const dateB = new Date(b.date_time).getTime() || 0;
            return dateB - dateA;
          });

          const mappedSlides = sortedData.map((item: any) => {
            const displayListTitle =
              useEnglish && item.list_title_en
                ? item.list_title_en
                : item.list_title || 'No title';
            const displayTitle =
              useEnglish && item.title_en ? item.title_en : item.title || '';
            return {
              imageSrc: item.header_image_url || Picture,
              width: 300,
              height: 300,
              linkText: displayListTitle,
              date: item.date_time || '',
              description: displayTitle,
              readStoryLink: `/magazine/${item.neighborhood_id}`,
              tags: item.tags || [],
            } as SlideData;
          });

          setSlidesData(mappedSlides);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch neighborhood list:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [useEnglish]);

  return (
    <div>
      <div className="text-center mb-11 mt-24 text-lg">( LATEST STORIES )</div>
      <div className="container mx-auto">
        <div className="md:grid md:grid-cols-3">
          {/* 左側の余白用カラム（そのまま残す） */}
          <div className="hidden md:block"></div>

          {/* スライダーを右端まで全幅表示 */}
          <div className="col-span-2 w-full">
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <Swiper
                modules={[Pagination]}
                spaceBetween={10}
                slidesPerView="auto"
                pagination={{ clickable: true }}
                speed={800}
                loop={false}
                centeredSlides={false}
                className="w-full"
              >
                {slidesData.map((slide, index) => (
                  <SwiperSlide
                    key={index}
                    className="flex-shrink-0"
                    style={{ width: `${slide.width}px` }}
                  >
                    <SlideItem {...slide} useEnglish={useEnglish} />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
